import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { apiGet } from "@/lib/client/api";
import { formatRmb } from "@/lib/pricing/format";
import { workbenchRoutes } from "@/lib/routes/workbench-routes";

type DashboardResponse = {
  totals: {
    quotations: number;
    grandTotalMinor: number;
  };
  statusCounts: Array<{
    status: string;
    count: number;
  }>;
  recentQuotations: Array<{
    id: string;
    quotationNo: string;
    projectTitle: string;
    status: string;
    updatedAt: number;
    grandTotalMinor: number | null;
    capabilities: { canEdit: boolean };
  }>;
};

const trackedStatuses = ["draft", "internal_review", "sent", "approved"] as const;

export function DashboardPage() {
  const { t } = useAppSettings();
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const statusCountMap = useMemo(() => {
    const map = new Map<string, number>();

    for (const row of dashboard?.statusCounts ?? []) {
      map.set(row.status, row.count);
    }

    return map;
  }, [dashboard]);

  useEffect(() => {
    let active = true;

    async function loadDashboard() {
      setLoading(true);
      setError(null);

      try {
        const response = await apiGet<DashboardResponse>("/api/dashboard");

        if (active) {
          setDashboard(response);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : t("dashboard.loadError"));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    void loadDashboard();

    return () => {
      active = false;
    };
  }, [t]);

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("dashboard.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("dashboard.description")}</p>
        </div>
        <Link
          to={workbenchRoutes.newQuotation}
          className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
        >
          {t("dashboard.newQuote")}
        </Link>
      </div>
      {error ? <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div> : null}
      <div className="grid gap-3 md:grid-cols-4">
        {trackedStatuses.map((status) => (
          <Card key={status} className="p-4">
            <Badge>{getStatusLabel(status, t)}</Badge>
            <div className="mt-4 text-2xl font-semibold">{loading ? "-" : statusCountMap.get(status) ?? 0}</div>
          </Card>
        ))}
      </div>
      <div className="grid gap-3 md:grid-cols-[1fr_2fr]">
        <Card className="p-4">
          <div className="text-sm font-medium text-muted-foreground">{t("dashboard.totalPipeline")}</div>
          <div className="mt-2 text-2xl font-semibold">{loading ? "-" : formatRmb(dashboard?.totals.grandTotalMinor ?? 0)}</div>
          <div className="mt-1 text-xs text-muted-foreground">{loading ? "-" : `${dashboard?.totals.quotations ?? 0} ${t("dashboard.quotationCount")}`}</div>
        </Card>
        <Card className="overflow-hidden">
          <div className="border-b border-border px-4 py-3 text-sm font-semibold">{t("dashboard.recentQuotations")}</div>
          {loading ? <div className="px-4 py-8 text-sm text-muted-foreground">{t("dashboard.loading")}</div> : null}
          {!loading && dashboard?.recentQuotations.length === 0 ? <div className="px-4 py-8 text-sm text-muted-foreground">{t("dashboard.empty")}</div> : null}
          {!loading
            ? dashboard?.recentQuotations.map((quotation) => (
                <Link
                  key={quotation.id}
                  to={quotation.capabilities.canEdit ? workbenchRoutes.quotationEdit(quotation.id) : workbenchRoutes.quotationPreview(quotation.id)}
                  className="grid grid-cols-[1fr_auto] gap-3 border-b border-border px-4 py-3 text-sm transition last:border-b-0 hover:bg-muted"
                >
                  <span>
                    <span className="font-medium">{quotation.quotationNo}</span>
                    <span className="ml-2 text-muted-foreground">{quotation.projectTitle}</span>
                  </span>
                  <span className="text-muted-foreground">{formatRmb(quotation.grandTotalMinor ?? 0)}</span>
                </Link>
              ))
            : null}
        </Card>
      </div>
    </section>
  );
}

function getStatusLabel(status: string, t: ReturnType<typeof useAppSettings>["t"]): string {
  const labels: Record<string, string> = {
    draft: t("status.draft"),
    internal_review: t("status.internalReview"),
    sent: t("status.sent"),
    approved: t("status.approved"),
  };

  return labels[status] ?? status;
}
